import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { Subscription } from 'rxjs';

const NOEVENT = { emitEvent: false };

export interface TemplateChangeEvent {
  template: TemplateModel | null;
  location: LocationModel | null;
  tags: Array<string>;
}

@Component({
  selector: 'app-orderform-template',
  templateUrl: './orderform-template.component.html',
  styleUrls: ['./orderform-template.component.scss']
})
export class PlannerOrderformTemplateComponent implements OnInit, OnChanges, OnDestroy {

  @Input() form: UntypedFormGroup;
  @Input() options: { [key: string]: Array<SelectItem | SelectItemGroup> };
  @Input() templates: Array<TemplateModel> = [];
  @Input() businessunit: BusinessunitModel = null;
  @Input() texts: { [key: string]: string };
  @Input() disabled = true;

  @Output() changed = new EventEmitter<TemplateChangeEvent | null>();

  private subscriptions: Array<Subscription> = [];
  private virtualInfoShown = false;
  public virtualInfoVisible = false;

  private subForm: UntypedFormGroup = this.fb.group({
    base: this.fb.group({
      template: new UntypedFormControl(null, [Validators.required]),
      location: new UntypedFormControl(null, [Validators.required]),
    }),
    add: this.fb.group({
      lqgposition: new UntypedFormControl(''),
      tags: new UntypedFormControl(''),
    }),
  });

  public locations: Array<any> = [];
  public lqgPositionVisible = false;
  public tagVisible = false;
  public tagLabel = null;
  public tagDescription = null;
  public tags: Array<string> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private auth: AuthStateService,
    private helper: HelperService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.form.addControl('template', this.subForm);
    // track template changes
    let oldTemplate = this.subForm.get('base.template').value;
    this.subForm.get('base.template').valueChanges.subscribe((template: TemplateModel) => {
      let location: LocationModel = null;
      if (
        template !== null &&
        template !== oldTemplate
      ) {
        // set locations
        const locations = this.options?.locations.map((location: any) => {
          location.name = (location.name?.includes('common.')) ? this.transloco.translate(location.name) : location.name;
          return new LocationModel().deserialize(location);
        }).filter((location: LocationModel) => {
          // filter on onsite/virtual
          let onsite = template.onsite;
          let virtual = template.virtual;
          if (this.businessunit && template.businessunits?.length > 0) {
            // overwrite template settings with businessunit settings
            const businessunit = template.businessunits.find(actual => actual.id === this.businessunit.id);
            if (businessunit.pivot.settings['onsite'] !== undefined) {
              onsite = !!businessunit.pivot.settings['onsite'];
            }
            if (businessunit.pivot.settings['virtual'] !== undefined) {
              virtual = !!businessunit.pivot.settings['virtual'];
            }
          }
          return location.other !== true && (onsite === true && location.virtual !== true || virtual === true && location.virtual === true);
        }).filter((location: LocationModel) => {
          // fiter on location
          return template.locations.length === 0 || template.locations?.find(actual => actual.id === location.id) !== undefined;
        }).sort((a: LocationModel, b: LocationModel) => {
          if (a.virtual !== b.virtual) {
            return b.virtual === true ? 1 : -1;
          }
          return a.name.localeCompare(b.name);
        });
        this.locations = locations;
        location = this.locations.find(actual => actual.id === this.subForm.get('base.location').value?.id) || null;
        if (location === null && this.locations.length === 1) {
          this.subForm.get('base.location').setValue(this.locations[0]);
        }
        // set tag field and options
        const lang = this.transloco.getActiveLang().toUpperCase();
        if (this.auth.hasFeature('templates.tags') && template.tags?.length > 0) {
          this.tags = template.tags;
          this.tagVisible = true;
          this.subForm.get('add.tags').setValue(this.tags);
          if (template.tags_required === true) {
            this.subForm.get('add.tags').addValidators([this.tagsRequiredValidator()]);
          } else {
            this.subForm.get('add.tags').clearValidators();
            this.subForm.get('add.tags').setErrors(null);
          }
          this.tagLabel = template.tag_label?.[lang || 'DE'] || this.transloco.translate('common.labels.tags');
          this.tagDescription = template.tag_description?.[lang || 'DE'] || null;
          this.subForm.get('add.tags').updateValueAndValidity();
        } else {
          this.tagVisible = false;
          this.tagLabel = null;
          this.tagDescription = null;
          this.subForm.get('add.tags').setValue(null);
          this.subForm.get('add.tags').clearValidators();
          this.subForm.get('add.tags').setErrors(null);
        }
      }
      const tags = this.getTagsFromForm(template);
      this.emitData(template, location, tags);
      oldTemplate = template;
    });
    // track location changes
    let oldLocation = this.subForm.get('base.location').value;
    this.subForm.get('base.location').valueChanges.subscribe((location: LocationModel) => {
      const template = this.subForm.get('base.template').value || null;
      const tags = this.getTagsFromForm(template);
      if (
        location !== null &&
        location !== oldLocation
      ) {
        this.emitData(template, location, tags);
        oldLocation = location;
        const hasVirtual = this.locations.find(actual => actual.virtual === true) || false;
        // show location Info window
        if (location.virtual === true) {
          this.virtualInfoShown = false;
        } else if (hasVirtual && this.virtualInfoShown === false) {
            this.virtualInfoVisible = true;
            this.virtualInfoShown = true;
        }
      }
    });
    // track tags changes
    let oldTags = this.subForm.get('add.tags').value;
    this.subForm.get('add.tags').valueChanges.subscribe(() => {
      const template = this.subForm.get('base.template').value || null;
      const location = this.locations.find(actual => actual.id === this.subForm.get('base.location').value?.id) || null;
      const tags = this.getTagsFromForm(template);
      if (this.tagsChanged(oldTags, tags)) {
        this.emitData(template, location, tags);
        oldTags = tags;
      }
    });
    this.disable();
  }

  private emitData(template: TemplateModel | null, location: LocationModel | null, tags: Array<string>): void {
    this.changed.emit({
      template,
      location,
      tags,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled && changes.disabled.currentValue !== changes.disabled.previousValue) {
      if (changes.disabled.currentValue === true) {
        this.disable();
      } else {
        this.enable();
      }
    }
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public setSettings(templateSettings: any, crossSettings: any): void {
    if (templateSettings.lqgposition === true) {
      this.lqgPositionVisible = true;
      this.subForm.get('add.lqgposition').setValidators([Validators.required]);
    } else {
      this.subForm.get('add.lqgposition').clearValidators();
      this.lqgPositionVisible = false;
    }
    this.subForm.get('add.lqgposition').updateValueAndValidity();
  }

  public disable(): void {
    this.subForm.get('base.template').disable(NOEVENT);
    this.subForm.get('base.location').disable();
  }

  public enable(): void {
    this.subForm.get('base.template').enable(NOEVENT);
    this.subForm.get('base.location').enable();
  }

  public getPayload(): any {
    return {
      template: this.subForm.get('base.template').value?.id,
      location: this.subForm.get('base.location').value?.id,
      lqgposition: this.subForm.get('add.lqgposition').value,
      tags: this.subForm.get('add.tags').value || null,
    };
  }

  public acceptVirtualInfo(): void {
    this.virtualInfoVisible = false;
    this.virtualInfoShown = true;
    const virtual = this.locations.find(actual => actual.virtual === true) || null;
    if (virtual !== null) {
      this.subForm.get('base.location').setValue(virtual);
    }
  }

  public declineVirtualInfo(): void {
    this.virtualInfoVisible = false;
    this.virtualInfoShown = true;
  }

  private getTagsFromForm(template: TemplateModel): Array<string> {
    const formValue = this.subForm.get('add.tags').value;
    if (Array.isArray(formValue)) {
      return template?.tags?.filter(tag => formValue.includes(tag)) || [];
    }
    return [];
  }

  private tagsChanged(oldTags: Array<string>, newTags: Array<string>): boolean {
    if (!Array.isArray(oldTags)) {
      oldTags = [];
    }
    if (!Array.isArray(newTags)) {
      newTags = [];
    }
    if (oldTags.length !== newTags.length) {
      return true;
    }
    return oldTags.filter(tag => newTags.includes(tag)).length !== oldTags.length;
  }

  private tagsRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return { required: true };
      }
      return null;
    };
  }

}
